import { createContext, useEffect, useMemo, useState } from 'react';

export const CartContext = createContext(null);

const CART_STORAGE_KEY = 'ogc-cart-v1';

function readStoredCart() {
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function persistCart(items) {
  try {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    // Ignorar errores de cuota o modo privado.
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readStoredCart);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    persistCart(items);
  }, [items]);

  function addItem(product, quantity = 1) {
    setItems((current) => {
      const existing = current.find((item) => item.productId === product.id);

      if (existing) {
        return current.map((item) =>
          item.productId === product.id ? { ...item, quantity: item.quantity + quantity } : item,
        );
      }

      return [
        ...current,
        {
          productId: product.id,
          name: product.name,
          image: product.image,
          price: product.price,
          weight: product.weight,
          category: product.category,
          quantity,
        },
      ];
    });
    setIsCartOpen(true);
  }

  function removeItem(productId) {
    setItems((current) => current.filter((item) => item.productId !== productId));
  }

  function incrementItem(productId) {
    setItems((current) =>
      current.map((item) => (item.productId === productId ? { ...item, quantity: item.quantity + 1 } : item)),
    );
  }

  function decrementItem(productId) {
    setItems((current) =>
      current
        .map((item) => (item.productId === productId ? { ...item, quantity: item.quantity - 1 } : item))
        .filter((item) => item.quantity > 0),
    );
  }

  function clearCart() {
    setItems([]);
  }

  function openCart() {
    setIsCartOpen(true);
  }

  function closeCart() {
    setIsCartOpen(false);
  }

  const totalItems = useMemo(() => items.reduce((sum, item) => sum + item.quantity, 0), [items]);
  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity * Number(item.price || 0), 0),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      totalItems,
      totalAmount,
      isCartOpen,
      addItem,
      removeItem,
      incrementItem,
      decrementItem,
      clearCart,
      openCart,
      closeCart,
    }),
    [items, totalItems, totalAmount, isCartOpen],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
